import { useFormikContext } from 'formik'
import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { Form } from 'src/views/components/forms/formik/Form'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import FormikTinyMce from 'src/views/components/forms/FormikTinyMce'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface FormikValues {
  readonly name: string
  readonly nameEn: string
  readonly description: string
  readonly descriptionEn: string
  readonly status?: string
}

enum EntityType {
  Competencies = 'competencies',
  Criterias = 'criterias',
}

const STATUS_OPTIONS = [
  { value: '1', label: 'common:active' },
  { value: '2', label: 'common:inactive' },
] as const

interface EntityData {
  readonly name: string
  readonly nameEn: string
  readonly description: string
  readonly descriptionEn: string
  readonly status: { value: string; name: string }
}

function useEntityData(
  id: string | undefined,
  entityType: EntityType,
  headers: ReturnType<typeof useAuthenticatedHeaders>
): EntityData | null {
  const competencyData = useApi({
    endpoint: Api.getAdministratorPortfolioCompetency,
    params: React.useMemo(() => ({ headers, args: { id: id! } }), [headers, id]),
    shouldFetch: id != null && entityType === EntityType.Competencies,
  })

  const criteriaData = useApi({
    endpoint: Api.getAdministratorPortfolioCriteria,
    params: React.useMemo(() => ({ headers, args: { id: id! } }), [headers, id]),
    shouldFetch: id != null && entityType === EntityType.Criterias,
  })

  const data = entityType === EntityType.Competencies ? competencyData.data : criteriaData.data

  return data ?? null
}

function getApiFunctions(entityType: EntityType) {
  return {
    post:
      entityType === 'competencies' ? Api.postAdministratorPortfolioCompetency : Api.postAdministratorPortfolioCriteria,
    put:
      entityType === 'competencies' ? Api.putAdministratorPortfolioCompetency : Api.putAdministratorPortfolioCriteria,
  }
}

export default function AdministratorPortfolioCompetencyCriteriaPage() {
  return (
    <SuspenseWrapper>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent() {
  const [successfullySubmited, setSuccessfullySubmited] = React.useState(false)
  const t = useTranslatable()
  const { id } = useParams<{ id?: string }>()
  const headers = useAuthenticatedHeaders()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const locale = useLocale()

  const entityType: EntityType = React.useMemo(
    () => (pathname.includes('competencies') ? EntityType.Competencies : EntityType.Criterias),
    [pathname]
  )

  const data = useEntityData(id, entityType, headers)
  const apiFunctions = React.useMemo(() => getApiFunctions(entityType), [entityType])

  const initialValues = React.useMemo<FormikValues>(
    () => ({
      name: data?.name ?? '',
      nameEn: data?.nameEn ?? '',
      description: data?.description ?? '',
      descriptionEn: data?.descriptionEn ?? '',
      status: data?.status.value ?? undefined,
    }),
    [data]
  )

  const handleSubmit = React.useCallback(
    async (values: FormikValues): Promise<void> => {
      try {
        const body = {
          name: values.name,
          nameEn: values.nameEn,
          description: values.description,
          descriptionEn: values.descriptionEn,
          status: Number(values.status),
        }

        if (id) {
          await apiFunctions.put({
            headers,
            body,
            args: { id },
          })
        } else {
          await apiFunctions.post({
            headers,
            body,
          })
        }

        toast.success(t('common:updated_successfully'))
        setSuccessfullySubmited(true)
      } catch (error) {
        console.error(error)
        toast.error(t('error:an_error_occurred'))
      }
    },
    [headers, id, apiFunctions, t]
  )

  const redirectPath = React.useMemo(
    () => `/${locale}/administrator/portfolio/${entityType === 'competencies' ? 'competencies' : 'criterias'}`,
    [locale, entityType]
  )

  React.useEffect(() => {
    if (successfullySubmited) {
      navigate(redirectPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfullySubmited])

  const entityTypeKey = entityType === EntityType.Competencies ? 'competencies' : 'competency_criterias'

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: t('portfolio:portfolio'), path: `/${locale}/administrator/portfolio` },
      { page: t(`portfolio:${entityTypeKey}`), path: `/${locale}/administrator/portfolio/${entityTypeKey}` },
      ...(data?.name ? [{ page: data.name, path: `/${locale}/administrator/portfolio/${entityTypeKey}/${id}` }] : []),
    ],
    [t, locale, entityTypeKey, data?.name, id]
  )

  const sectionTitle = React.useMemo(
    () => `${t('portfolio:portfolio')}/${t(`portfolio:${entityTypeKey}`)}${data?.name ? `/${data.name}` : ''}`,
    [t, entityTypeKey, data?.name]
  )

  return (
    <Section title={sectionTitle} icon={<PortfolioIcon />} breadcrubms={breadcrumbsItems} rightElement={<BackButton />}>
      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        classNames={{ form: 'w-full' }}
        isConfirmable={!successfullySubmited}
      >
        <FormContent />
      </Form>
    </Section>
  )
}

function FormContent(): JSX.Element {
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const formik = useFormikContext<FormikValues>()

  const statusOptions = React.useMemo(
    () => STATUS_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) })),
    [t]
  )

  return (
    <div className="mt-6 w-full rounded-card bg-card px-6 py-9">
      <div className="mb-2">
        <TextareaInput name="name" label={t('common:name')} rows={5} required />
      </div>
      <div className="mb-2">
        <TextareaInput name="nameEn" label={t('common:name_en')} rows={5} required />
      </div>

      <div className="mb-2">
        <SelectInput name="status" label={t('portfolio:work_type')} options={statusOptions} required />
      </div>

      <div className="mb-2">
        <FormikTinyMce name="description" label={t('portfolio:description')} required />
      </div>
      <div className="mb-2">
        <FormikTinyMce name="descriptionEn" label={t('portfolio:description_en')} required />
      </div>
      {canManagePortfolio && (
        <div className="mt-4 flex flex-wrap justify-end">
          <Button type="submit" className="ml-2" variant="red" disabled={!formik.isValid || !formik.dirty}>
            {t('common:save')}
          </Button>
        </div>
      )}
    </div>
  )
}
